import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import notificationService from '../../services/notificationService';
import { dispatchWorkflowAction } from '../../workflow/dispatchWorkflowAction';
import {
  applyMilestoneStatusChange,
  applyPhaseStatusChange,
  generateLotBatchNumber,
} from '../../workflow/caseTimelinePhaseMutations';
import { buildPhaseWorkflowHandlersMap } from '../../workflow/phaseWorkflowHandlersFactory';
import {
  fetchMilestoneTemplateActionsForRuntime,
} from '../../services/workflowRuntimeService';
import { fetchTemplateNameOptionsByMilestoneIds } from '../../services/milestoneNameOptionsService';
import { fetchWorkflowActionDefinitions } from '../../services/workflowAdminService';
import {
  pickActiveProductionPhase,
} from '../../services/caseProductionPhasesService';
import IPRMilestoneModal from '../case-detail/components/IPRMilestoneModal';
import AssignUserModal from '../cases-list/components/AssignUserModal';
import AssignPackageMilestoneModal from '../case-detail/components/AssignPackageMilestoneModal';
import {
  isProductionManagerRole,
  isSinglePhaseWorkshopStation,
  isWorkshopAtelierParticipantRole,
} from '../../lib/operatorRoles';
import {
  getAssignOperatorModalCopy,
  getAssignOperatorRoleTypesForPhase,
} from '../../lib/phaseOperatorAssignmentRoles';
import {
  filterMilestonesForOperator,
  normalizeWorkshopRoleType,
  pickWorkshopPhaseForRole,
  userHasExpandedWorkshopView,
  WORKSHOP_OPERATOR_PHASE_NAME_HINTS,
  WORKSHOP_OPERATOR_ROLE_LABELS,
} from '../../lib/workshopOperatorView';
import WorkshopPhaseStation from './WorkshopPhaseStation';
import WorkshopMilestoneActionSheet from './WorkshopMilestoneActionSheet';
import {
  MILESTONE_MODAL_TYPES,
  resolveMilestoneModalType,
} from './milestoneModalConfig';
import {
  buildMilestoneMetaFromPayload,
  buildRejectMilestoneMeta,
  getMilestoneInteractionKind,
  isPrintingFinishedMilestone,
  MILESTONE_INTERACTION,
  resolvePrintingOutcomeForPhase,
} from '../../lib/milestoneOutcomeUtils';
import {
  assertValidMilestoneNameChoice,
  requiresMilestoneNameSelection,
} from '../../lib/milestoneNameOptions';
import {
  AssignmentLockConflictError,
  assignCaseOperatorRevisionRpc,
  isAdministratorActor,
  toastAssignmentLockConflict,
} from '../../lib/caseAssignmentLock';

const WORKFLOW_DISPATCH_ATELIER =
  import.meta.env.VITE_ATELIER_WORKFLOW_DISPATCH === 'true' ||
  import.meta.env.VITE_ENABLE_WORKFLOW_DISPATCH === 'true';

function getGroup(phase) {
  return String(phase?.group ?? '');
}

function phaseStepClass(status, isActive) {
  if (status === 'completed') return 'bg-success text-white';
  if (status === 'in_progress' || isActive) return 'bg-primary text-primary-foreground';
  if (status === 'cancelled') return 'bg-destructive/80 text-white';
  return 'bg-muted text-muted-foreground';
}

/**
 * Parcours phases/jalons production (même groupe que le lot) + actions workflow.
 */
export default function WorkshopProductionPhasesPanel({
  batch,
  caseId,
  caseData,
  phases,
  userRole,
  onRefresh,
}) {
  const navigate = useNavigate();
  const actorRoleTypes = useMemo(() => [userRole].filter(Boolean), [userRole]);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [updatingPhaseId, setUpdatingPhaseId] = useState(null);

  const [iprModalOpen, setIprModalOpen] = useState(false);
  const [iprMilestoneId, setIprMilestoneId] = useState(null);

  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteMilestoneId, setNoteMilestoneId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignModalPhase, setAssignModalPhase] = useState(null);
  const [assignModalMilestone, setAssignModalMilestone] = useState(null);

  const [assignOperatorModalOpen, setAssignOperatorModalOpen] = useState(false);
  const [assignOperatorMilestone, setAssignOperatorMilestone] = useState(null);
  const [assignOperatorPhase, setAssignOperatorPhase] = useState(null);

  const [assignPackageModalOpen, setAssignPackageModalOpen] = useState(false);
  const [assignPackagePhase, setAssignPackagePhase] = useState(null);
  const [assignPackageMilestone, setAssignPackageMilestone] = useState(null);

  const [workflowDefMap, setWorkflowDefMap] = useState({});
  const [templateActionsByTemplateId, setTemplateActionsByTemplateId] = useState({});
  const [templateNameOptionsByTemplateId, setTemplateNameOptionsByTemplateId] = useState({});

  const [milestoneSheetOpen, setMilestoneSheetOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [savingMilestone, setSavingMilestone] = useState(false);

  const canUseMilestones =
    userRole === 'administrator' || isProductionManagerRole(userRole) || isWorkshopAtelierParticipantRole(userRole);

  /** Phase « courante » dans le workflow (surlignage parcours complet). */
  const workflowAnchorPhase = useMemo(() => pickActiveProductionPhase(phases), [phases]);

  /** Vue étendue : admin / pilotes / ancien « operators ». Sinon : une phase = un poste. */
  const expandedWorkshopView = userHasExpandedWorkshopView(userRole);

  const displayPhase = useMemo(() => {
    if (!phases?.length) return null;
    return expandedWorkshopView
      ? workflowAnchorPhase
      : pickWorkshopPhaseForRole(phases, userRole);
  }, [phases, userRole, expandedWorkshopView, workflowAnchorPhase]);

  const phaseAlignedWithPost = useMemo(() => {
    const stationRole = normalizeWorkshopRoleType(userRole) || userRole;
    if (!displayPhase || !isSinglePhaseWorkshopStation(stationRole)) return true;
    const hints = WORKSHOP_OPERATOR_PHASE_NAME_HINTS[stationRole];
    if (!hints?.length) return true;
    const n = String(displayPhase.name || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return hints.some((h) => n.includes(h));
  }, [displayPhase, userRole]);

  const displayMilestones = useMemo(() => {
    const msRaw = displayPhase?.milestones || [];
    return filterMilestonesForOperator(
      msRaw,
      userRole,
      templateActionsByTemplateId,
      WORKFLOW_DISPATCH_ATELIER
    );
  }, [displayPhase, userRole, templateActionsByTemplateId]);

  const selectedTemplateActions = useMemo(() => {
    if (!selectedMilestone?.milestone_template_id) return [];
    return templateActionsByTemplateId[selectedMilestone.milestone_template_id] || [];
  }, [selectedMilestone, templateActionsByTemplateId]);

  const selectedModalType = useMemo(
    () => resolveMilestoneModalType(selectedMilestone, selectedTemplateActions),
    [selectedMilestone, selectedTemplateActions]
  );

  useEffect(() => {
    setMilestoneSheetOpen(false);
    setSelectedMilestone(null);
  }, [displayPhase?.id]);

  useEffect(() => {
    if (!WORKFLOW_DISPATCH_ATELIER) return;
    fetchWorkflowActionDefinitions()
      .then((defs) => {
        const map = {};
        for (const d of defs) map[d.action_key] = d;
        setWorkflowDefMap(map);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!phases?.length) {
      setTemplateActionsByTemplateId({});
      setTemplateNameOptionsByTemplateId({});
      return;
    }
    const ids = new Set();
    phases.forEach((p) =>
      (p.milestones || []).forEach((m) => {
        if (m.milestone_template_id) ids.add(m.milestone_template_id);
      })
    );
    if (ids.size === 0) {
      setTemplateActionsByTemplateId({});
      setTemplateNameOptionsByTemplateId({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const templateIds = [...ids];
        const loadActions = WORKFLOW_DISPATCH_ATELIER
          ? Promise.all(
              templateIds.map(async (tid) => {
                const rows = await fetchMilestoneTemplateActionsForRuntime(tid);
                return [tid, rows];
              })
            )
          : Promise.resolve([]);
        const [pairs, optionsMap] = await Promise.all([
          loadActions,
          fetchTemplateNameOptionsByMilestoneIds(templateIds),
        ]);
        if (!cancelled) {
          setTemplateActionsByTemplateId(Object.fromEntries(pairs));
          setTemplateNameOptionsByTemplateId(optionsMap);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phases]);

  const handlePhaseStatusChange = async (phaseId, newStatus, phaseOrder, group) => {
    setUpdatingPhaseId(phaseId);
    try {
      await applyPhaseStatusChange(supabase, {
        caseId,
        caseData,
        phaseId,
        newStatus,
        phaseOrder,
        group,
        generateBatchNumber: generateLotBatchNumber,
        onPhaseUpdated: onRefresh,
      });
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la mise à jour: ' + err.message);
    } finally {
      setUpdatingPhaseId(null);
    }
  };

  const handleMilestoneStatusChange = async (milestoneId, newStatus, phase, milestoneMeta) => {
    await applyMilestoneStatusChange(supabase, {
      milestoneId,
      newStatus,
      phase,
      getGroup,
      runPhaseStatusChange: handlePhaseStatusChange,
      onPhaseUpdated: onRefresh,
      milestoneMeta,
    });
  };

  const handleOpenAssignModal = (phase, milestone) => {
    setAssignModalPhase(phase);
    setAssignModalMilestone(milestone);
    setAssignModalOpen(true);
  };

  const handleAssignProsthetist = async (userId) => {
    if (!assignModalPhase || !assignModalMilestone) return;
    const now = new Date().toISOString();
    const { error: caseError } = await supabase
      .from('cases')
      .update({ prosthetist_id: userId, updated_at: now })
      .eq('id', caseId);
    if (caseError) throw new Error("Erreur lors de l'assignation du prothésiste: " + caseError.message);
    await notificationService.createNotification({
      userId,
      type: 'info',
      title: 'Nouveau cas créé',
      message: `Un cas a été créé`,
      link: `/case-detail/${caseId}`,
    });
    const { error: phase3Error } = await supabase
      .from('case_timeline_phases')
      .update({ responsible_user_id: userId, updated_at: now })
      .eq('case_id', caseId)
      .eq('group', '1')
      .eq('phase_order', 3);
    if (phase3Error) console.warn('Phase 3 responsable:', phase3Error.message);

    const { error: msError } = await supabase
      .from('phase_milestones')
      .update({ is_completed: true, completed_date: now, updated_at: now })
      .eq('id', assignModalMilestone.id);
    if (msError) throw new Error('Erreur lors de la validation du jalon: ' + msError.message);

    const milestones = assignModalPhase?.milestones ?? [];
    const allNowCompleted = milestones.every((m) =>
      m.id === assignModalMilestone.id ? true : m.completed === true
    );

    if (allNowCompleted && milestones.length > 0) {
      toast.success('Prothésiste assigné et tous les jalons validés! Passage à la phase suivante…');
      setAssignModalOpen(false);
      await handlePhaseStatusChange(
        assignModalPhase.id,
        'completed',
        assignModalPhase.phase_order,
        getGroup(assignModalPhase)
      );
    } else {
      toast.success('Prothésiste assigné, responsable de la phase de conception mis à jour!');
      setAssignModalOpen(false);
      if (onRefresh) onRefresh();
    }
  };

  const handleOpenAssignOperatorModal = (phase, milestone) => {
    setAssignOperatorPhase(phase);
    setAssignOperatorMilestone(milestone);
    setAssignOperatorModalOpen(true);
  };

  const handleAssignOperator = async (userId, _fullName, { force = false } = {}) => {
    if (!assignOperatorMilestone) return;
    const targetRoleTypes = getAssignOperatorRoleTypesForPhase(assignOperatorPhase);
    try {
      await assignCaseOperatorRevisionRpc(supabase, {
        caseId,
        userId,
        milestoneId: assignOperatorMilestone.id,
        force,
        actorRoleTypes,
        targetRoleTypes,
      });

      await notificationService.createNotification({
        userId,
        type: 'info',
        title: 'Nouveau cas créé',
        message: 'Un cas a été créé',
        link: `/case-detail/${caseId}`,
      });

      toast.success('Opérateur assigné avec succès!');
      setAssignOperatorModalOpen(false);
      setAssignOperatorPhase(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      if (err instanceof AssignmentLockConflictError) {
        toastAssignmentLockConflict(toast, navigate, err);
      } else {
        toast.error(err.message);
      }
      throw err;
    }
  };

  const handleOpenAssignPackageModal = (phase, milestone) => {
    setAssignPackagePhase(phase);
    setAssignPackageMilestone(milestone);
    setAssignPackageModalOpen(true);
  };

  const handlePackageAssigned = async (packageId) => {
    if (!assignPackagePhase || !assignPackageMilestone) return;
    const phaseSnap = assignPackagePhase;
    const milestoneSnap = assignPackageMilestone;
    const now = new Date().toISOString();
    try {
      const { error: caseError } = await supabase
        .from('cases')
        .update({ package: packageId, updated_at: now })
        .eq('id', caseId);
      if (caseError) throw caseError;

      const { error: msError } = await supabase
        .from('phase_milestones')
        .update({ is_completed: true, completed_date: now, updated_at: now })
        .eq('id', milestoneSnap.id);
      if (msError) {
        toast.error('Package assigné mais erreur lors de la validation du jalon.');
        return;
      }

      const milestones = phaseSnap?.milestones ?? [];
      const allNowCompleted = milestones.every((m) =>
        m.id === milestoneSnap.id ? true : m.completed === true
      );

      setAssignPackageModalOpen(false);
      setAssignPackagePhase(null);
      setAssignPackageMilestone(null);

      if (allNowCompleted && milestones.length > 0) {
        toast.success('Package assigné et tous les jalons validés! Passage à la phase suivante…');
        await handlePhaseStatusChange(
          phaseSnap.id,
          'completed',
          phaseSnap.phase_order,
          getGroup(phaseSnap)
        );
      } else {
        toast.success('Package assigné et jalon validé!');
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      toast.warning("Veuillez entrer une note avant d'enregistrer.");
      return;
    }
    setSavingNote(true);
    try {
      const { error } = await supabase
        .from('phase_milestones')
        .update({ note: noteText.trim(), updated_at: new Date().toISOString() })
        .eq('id', noteMilestoneId);
      if (error) throw error;
      toast.success('Note enregistrée avec succès!');
      setNoteModalOpen(false);
      setNoteText('');
      setNoteMilestoneId(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement: " + err.message);
    } finally {
      setSavingNote(false);
    }
  };

  const openProductionSheetForMilestone = (milestone) => {
    if (!caseId) {
      toast.error('Cas introuvable pour ouvrir la fiche de production.');
      return;
    }
    const params = new URLSearchParams({ caseId: String(caseId) });
    if (batch?.id) params.set('batchId', batch.id);
    if (milestone?.id) params.set('milestoneId', milestone.id);
    window.open(`/production-sheet?${params.toString()}`, '_blank');
  };

  const saveMilestoneNote = async (milestoneId, note) => {
    if (!note?.trim()) return;
    const { error } = await supabase
      .from('phase_milestones')
      .update({ note: note.trim(), updated_at: new Date().toISOString() })
      .eq('id', milestoneId);
    if (error) throw new Error(error.message);
  };

  const handleMilestonePress = (milestone, state) => {
    if (state === 'locked') return;
    setSelectedMilestone(milestone);
    setMilestoneSheetOpen(true);
  };

  const handleValidateMilestone = async (milestone, phase, rawPayload) => {
    if (!canUseMilestones || phase?.status !== 'in_progress' || milestone.completed) return;

    let payload = { ...(rawPayload || {}) };

    const nameOptions = milestone.milestone_template_id
      ? templateNameOptionsByTemplateId[milestone.milestone_template_id] || []
      : [];

    if (requiresMilestoneNameSelection(nameOptions) && !payload.nameChoice) {
      // Clic Laser/Manuel : dériver le libellé catalogue depuis modeChoice
      if (payload.modeChoice) {
        const match = nameOptions.find(
          (o) =>
            o.code === payload.modeChoice ||
            String(o.label || '').toLowerCase() === payload.modeChoice
        );
        if (match) payload = { ...payload, nameChoice: match.label };
      }
      if (!payload.nameChoice) {
        toast.error('Choisissez une valeur dans la liste.');
        return;
      }
    }

    const kind = getMilestoneInteractionKind(milestone, phase);
    if (kind === MILESTONE_INTERACTION.OUTCOME && !payload?.outcomeChoice) {
      toast.error('Sélectionnez Complète, Partiel ou Échec.');
      return;
    }
    if (
      kind === MILESTONE_INTERACTION.OUTCOME &&
      payload?.outcomeChoice === 'echec' &&
      (payload.failedAligners == null || Number(payload.failedAligners) < 0)
    ) {
      toast.error('Indiquez le nombre d’aligneurs en échec.');
      return;
    }
    if (
      kind === MILESTONE_INTERACTION.CUT_MODE &&
      !payload?.modeChoice &&
      !payload?.nameChoice
    ) {
      toast.error('Choisissez Manuel ou Laser.');
      return;
    }

    setSavingMilestone(true);
    try {
      const actions = milestone.milestone_template_id
        ? templateActionsByTemplateId[milestone.milestone_template_id] || []
        : [];
      const modalType = resolveMilestoneModalType(milestone, actions);

      if (modalType === MILESTONE_MODAL_TYPES.PRODUCTION_SHEET) {
        setMilestoneSheetOpen(false);
        openProductionSheetForMilestone(milestone);
        return;
      }
      if (modalType === MILESTONE_MODAL_TYPES.IPR) {
        setMilestoneSheetOpen(false);
        setIprMilestoneId(milestone.id);
        setIprModalOpen(true);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      let milestoneMeta = buildMilestoneMetaFromPayload(milestone, payload, user?.id, phase);
      if (payload?.nameChoice) {
        milestoneMeta.milestone_name = assertValidMilestoneNameChoice(
          payload.nameChoice,
          nameOptions
        );
      }
      if (isPrintingFinishedMilestone(milestone.name)) {
        const inherited = resolvePrintingOutcomeForPhase(phase?.milestones, milestone);
        if (inherited && !milestoneMeta.completion_type) {
          milestoneMeta = { ...milestoneMeta, completion_type: inherited };
        }
      }
      if (milestoneMeta.validated_by_user_id) {
        milestoneMeta.validated_at = new Date().toISOString();
      }

      await handleMilestoneStatusChange(milestone.id, 'completed', phase, milestoneMeta);
      setMilestoneSheetOpen(false);
      setSelectedMilestone(null);
    } catch (err) {
      toast.error(err?.message || 'Validation impossible');
    } finally {
      setSavingMilestone(false);
    }
  };

  const handleRejectMilestone = async (milestone, phase, payload) => {
    if (!canUseMilestones || phase?.status !== 'in_progress') return;
    if (!payload?.rejectNote?.trim()) {
      toast.error('Un commentaire est obligatoire pour signaler un problème.');
      return;
    }
    setSavingMilestone(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const milestoneMeta = buildRejectMilestoneMeta(payload.rejectNote, user?.id);
      await handleMilestoneStatusChange(milestone.id, 'rejected', phase, milestoneMeta);
      toast.success('Problème signalé');
      setMilestoneSheetOpen(false);
      setSelectedMilestone(null);
    } catch (err) {
      toast.error(err?.message || 'Erreur');
    } finally {
      setSavingMilestone(false);
    }
  };

  const workflowApiFor = (phase, milestone) => ({
    submit_case: () =>
      toast.info('Soumettre la conception depuis la fiche détail du cas.'),
    open_stl_preview: () => toast.info('Aperçu STL disponible sur la fiche cas (onglet Fichiers).'),
    assign_operator: () => handleOpenAssignOperatorModal(phase, milestone),
    assign_prosthetist: () => handleOpenAssignModal(phase, milestone),
    assign_package: () => handleOpenAssignPackageModal(phase, milestone),
    open_ipr_milestone: () => {
      setIprMilestoneId(milestone.id);
      setIprModalOpen(true);
    },
    open_production_sheet: () => openProductionSheetForMilestone(milestone),
    add_milestone_note: () => {
      setNoteMilestoneId(milestone.id);
      setNoteText(milestone.note ?? '');
      setNoteModalOpen(true);
    },
    confirm_phase: () =>
      setPendingStatus({
        phaseId: phase?.id,
        phaseOrder: phase?.phase_order,
        group: getGroup(phase),
        value: 'completed',
        label: 'Confirmer',
      }),
    reject_phase: () =>
      setPendingStatus({
        phaseId: phase?.id,
        phaseOrder: phase?.phase_order,
        group: getGroup(phase),
        value: 'cancelled',
        label: 'Rejeter',
      }),
  });

  return (
    <div className="space-y-3">
      {!expandedWorkshopView && phases.length > 0 && (
        <div className="rounded-xl border border-border/70 bg-muted/25 px-3 py-2.5">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Vue poste opérateur</span>
            {' · '}
            Groupe lot {batch?.current_phase ?? '—'} — seuls votre étape et les objectifs de votre poste sont listés.
          </p>
        </div>
      )}

      {displayMilestones.length === 0 &&
      (displayPhase?.milestones?.length ?? 0) > 0 &&
      isSinglePhaseWorkshopStation(userRole) ? (
        <div className="rounded-lg border border-amber-500/35 bg-amber-500/[0.12] px-3 py-2 text-xs text-amber-950 dark:text-amber-100 leading-snug">
          Aucun objectif ne correspond à votre poste sur cette étape. Contactez un responsable production si vous
          devez intervenir ici.
        </div>
      ) : null}

      <WorkshopPhaseStation
        displayPhase={displayPhase}
        milestones={displayMilestones}
        templateActionsByTemplateId={templateActionsByTemplateId}
        phaseAlignedWithPost={phaseAlignedWithPost}
        operatorRoleLabel={
          !expandedWorkshopView && isSinglePhaseWorkshopStation(normalizeWorkshopRoleType(userRole) || userRole)
            ? WORKSHOP_OPERATOR_ROLE_LABELS[normalizeWorkshopRoleType(userRole) || userRole] ?? userRole
            : null
        }
        expandedWorkshopView={expandedWorkshopView}
        onMilestonePress={handleMilestonePress}
      />

      {expandedWorkshopView ? (
      <div className="rounded-xl border border-border bg-card p-3 space-y-2">
        <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
          Parcours complet — groupe {batch?.current_phase}
        </p>
        <div className="w-full overflow-x-auto pb-0.5 -mx-1 px-1 scroll-smooth touch-pan-x">
          <div className="flex items-center gap-0.5 min-w-max">
            {phases.map((p, i) => {
              const isActive = workflowAnchorPhase?.id === p.id;
              return (
                <React.Fragment key={p.id}>
                  {i > 0 && (
                    <div
                      className={`h-0.5 w-3 shrink-0 rounded ${
                        p.status === 'completed' || phases[i - 1]?.status === 'completed'
                          ? 'bg-primary'
                          : 'bg-muted'
                      }`}
                      aria-hidden
                    />
                  )}
                  <div
                    className={`flex flex-col items-center gap-0.5 px-1.5 py-1 rounded-lg min-w-[4.25rem] max-w-[5.5rem] ${
                      isActive ? 'ring-2 ring-primary/40 bg-primary/10' : ''
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${phaseStepClass(
                        p.status,
                        isActive
                      )}`}
                    >
                      {p.status === 'completed' ? '✓' : i + 1}
                    </div>
                    <span
                      className="text-[9px] font-medium text-center leading-tight text-muted-foreground line-clamp-2"
                      title={p.name}
                    >
                      {p.name}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      ) : null}

      {pendingStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-base font-semibold text-foreground mb-1">Confirmer le changement</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Mettre à jour le statut vers « {pendingStatus.label} » ?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingStatus(null)}
                className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  handlePhaseStatusChange(
                    pendingStatus.phaseId,
                    pendingStatus.value,
                    pendingStatus.phaseOrder,
                    pendingStatus.group
                  );
                  setPendingStatus(null);
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      <WorkshopMilestoneActionSheet
        open={milestoneSheetOpen}
        onClose={() => {
          setMilestoneSheetOpen(false);
          setSelectedMilestone(null);
        }}
        milestone={selectedMilestone}
        phase={displayPhase}
        modalType={selectedModalType}
        nameOptions={
          selectedMilestone?.milestone_template_id
            ? templateNameOptionsByTemplateId[selectedMilestone.milestone_template_id] || []
            : []
        }
        genericLabel={selectedMilestone?.name}
        readOnly={
          !canUseMilestones ||
          displayPhase?.status !== 'in_progress' ||
          selectedMilestone?.completed === true
        }
        saving={savingMilestone}
        onValidate={handleValidateMilestone}
        onReject={
          canUseMilestones && displayPhase?.status === 'in_progress' ? handleRejectMilestone : null
        }
        onOpenProductionSheet={(m) => {
          setMilestoneSheetOpen(false);
          openProductionSheetForMilestone(m);
        }}
        onOpenIpr={(m) => {
          setMilestoneSheetOpen(false);
          setIprMilestoneId(m.id);
          setIprModalOpen(true);
        }}
      />

      <IPRMilestoneModal
        isOpen={iprModalOpen}
        onClose={() => {
          setIprModalOpen(false);
          setIprMilestoneId(null);
        }}
        caseId={caseId}
        milestoneId={iprMilestoneId}
        onSaved={() => {
          if (onRefresh) onRefresh();
        }}
      />

      <AssignUserModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onAssign={handleAssignProsthetist}
        roleType="dentist_supplier"
        title="Assigner un prothésiste"
        description="Sélectionnez le prothésiste à assigner à cette conception."
      />

      <AssignUserModal
        isOpen={assignOperatorModalOpen}
        onClose={() => {
          setAssignOperatorModalOpen(false);
          setAssignOperatorPhase(null);
        }}
        onAssign={handleAssignOperator}
        roleType={getAssignOperatorRoleTypesForPhase(assignOperatorPhase)}
        title={getAssignOperatorModalCopy(assignOperatorPhase).title}
        description={getAssignOperatorModalCopy(assignOperatorPhase).description}
        allowForce={isAdministratorActor(actorRoleTypes)}
        assignmentContext="initial_review"
      />

      <AssignPackageMilestoneModal
        isOpen={assignPackageModalOpen}
        onClose={() => {
          setAssignPackageModalOpen(false);
          setAssignPackagePhase(null);
          setAssignPackageMilestone(null);
        }}
        dentistId={caseData?.dentistId}
        caseId={caseId}
        onSuccess={handlePackageAssigned}
      />

      {noteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-base font-semibold text-foreground mb-4">Note jalon</h3>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
              className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm outline-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setNoteModalOpen(false);
                  setNoteText('');
                  setNoteMilestoneId(null);
                }}
                className="px-4 py-2 text-sm rounded-lg border border-border"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSaveNote}
                disabled={savingNote}
                className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
